/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

'use client';

import { getSession, signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { useTranslation } from '@/lib/i18n';
import { logger } from '@/lib/logger';

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Login Page
 * Modern login page with Tailwind CSS styling
 */

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || '/';
  const message = searchParams.get('message') || '';
  const messageType = searchParams.get('type') || 'info';

  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        // translate known error codes
        if (result.error === 'INACTIVE_REGULAR') {
          setError(t('auth.inactiveRegular'));
        } else if (result.error === 'INACTIVE_BUSINESS') {
          setError(t('auth.inactiveBusiness'));
        } else {
          setError(result.error || t('common.error'));
        }
        logger.error({ msg: 'Login failed', error: result.error });
      } else {
        // Check if session was created successfully
        const session = await getSession();
        if (session) {
          logger.info({ msg: 'Login successful' });
          router.push(callbackUrl);
        } else {
          setError(t('common.error'));
        }
      }
    } catch (err) {
      setError(t('common.error'));
      logger.error({ msg: 'Login error', error: err });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">{t('auth.signIn')}</h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">{error}</div>}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth.email')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('auth.emailPlaceholder')}
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('auth.passwordPlaceholder')}
                  data-testid="login-password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link href="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                  {t('auth.forgotPassword.link')}
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                data-testid="login-submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('common.loading') : t('auth.signIn')}
              </button>
            </div>
          </form>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md text-center">
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.noAccount')}{' '}
            <Link href="/register" className="font-medium text-blue-600 hover:text-blue-500">
              {t('auth.signUp')}
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            {/* {t('auth.businessSignUpPrompt')}{' '} */}
            <Link href="/register-business" className="font-medium text-blue-600 hover:text-blue-500">
              {t('auth.registerBusiness')}
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            {/* {t('auth.businessLoginPrompt')}{' '} */}
            <Link href="/login-business" className="font-medium text-blue-600 hover:text-blue-500">
              {t('auth.businessLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
